import React from "react";
import { HOME_THEME as T } from "../../lib/homeTheme";

/** Oculto por defecto; se construye secuencialmente al hover del .group padre */
const REVEAL =
  "block rounded-full opacity-0 scale-50 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:scale-100";

const DELAYS = [
  "delay-0",
  "delay-75",
  "delay-150",
  "delay-200",
  "delay-300",
  "delay-[350ms]",
  "delay-[400ms]",
] as const;

export type HubModuleVisualId =
  | "guitar"
  | "scales"
  | "harmony"
  | "chords"
  | "modos"
  | "notas"
  | "melodies"
  | "songbook"
  | "library";

interface HubModuleVisualProps {
  moduleId: HubModuleVisualId | string;
}

/** Técnica — 4 puntos horizontales menta / lavanda */
function GuitarTechniqueGlyph() {
  const colors = [
    "bg-emerald-300 group-hover:shadow-[0_0_10px_rgba(110,231,183,0.9)]",
    "bg-[#c4b5fd] group-hover:shadow-[0_0_10px_rgba(196,181,253,0.9)]",
    "bg-emerald-300 group-hover:shadow-[0_0_10px_rgba(110,231,183,0.85)]",
    "bg-[#c4b5fd] group-hover:shadow-[0_0_10px_rgba(196,181,253,0.85)]",
  ];
  return (
    <div className="flex h-10 w-16 shrink-0 items-center justify-end gap-2" aria-hidden>
      {colors.map((color, i) => (
        <span
          key={i}
          className={`${REVEAL} h-1.5 w-1.5 ${DELAYS[i]} ${color}`}
        />
      ))}
    </div>
  );
}

/** Armonía — triángulo de 3 voces */
function HarmonyGlyph() {
  const nodes = [
    { className: "left-1/2 top-0 -translate-x-1/2", delay: "delay-0", bg: "bg-[#c4b5fd]", glow: "group-hover:shadow-[0_0_14px_rgba(196,181,253,0.95)]" },
    { className: "left-0 bottom-0", delay: "delay-150", bg: "bg-emerald-300", glow: "group-hover:shadow-[0_0_12px_rgba(110,231,183,0.9)]" },
    { className: "right-0 bottom-0", delay: "delay-75", bg: "bg-white", glow: "group-hover:shadow-[0_0_12px_rgba(255,255,255,0.85)]" },
  ];
  return (
    <div className="relative h-10 w-10 shrink-0" aria-hidden>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative h-6 w-6">
          {nodes.map((n, i) => (
            <span
              key={i}
              className={`absolute ${n.className} ${REVEAL} h-1.5 w-1.5 ${n.delay} ${n.bg} ${n.glow}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Mis partituras — líneas de pentagrama que se dibujan al hover */
function PartiturasGlyph() {
  const lineDelays = ["delay-75", "delay-150", "delay-200", "delay-300"] as const;
  return (
    <div className="flex h-10 w-16 shrink-0 flex-col items-end justify-center gap-2" aria-hidden>
      {lineDelays.map((delay, i) => (
        <div
          key={i}
          className={`h-px bg-white/30 w-0 transition-all duration-500 ease-out group-hover:w-12 ${delay}`}
        />
      ))}
    </div>
  );
}

/** Escalas — diagonal ascendente; tónica roja pastel + blancos eléctricos */
function ScalesGlyph() {
  const lifts = [0, 4, 8, 12, 16];
  return (
    <div className="flex h-10 w-14 shrink-0 items-end justify-end gap-2" aria-hidden>
      {lifts.map((lift, i) => (
        <span key={i} className="inline-flex shrink-0" style={{ transform: `translateY(-${lift}px)` }}>
          <span
            className={`${REVEAL} h-2 w-2 ${DELAYS[i]} ${
              i === 0
                ? "bg-[#f0a8a0] group-hover:shadow-[0_0_12px_rgba(240,168,160,0.9)]"
                : "bg-white group-hover:shadow-[0_0_10px_rgba(255,255,255,0.85)]"
            }`}
          />
        </span>
      ))}
    </div>
  );
}

/** 02 Acordes — triada; revelación de abajo hacia arriba */
function ChordsGlyph() {
  const nodes = [
    { delay: "delay-150", glow: "group-hover:shadow-[0_0_12px_rgba(251,191,36,0.85)]", bg: "bg-amber-300" },
    { delay: "delay-75", glow: "group-hover:shadow-[0_0_10px_rgba(255,255,255,0.8)]", bg: "bg-white" },
    { delay: "delay-0", glow: "group-hover:shadow-[0_0_12px_rgba(196,181,253,0.85)]", bg: "bg-purple-400" },
  ];
  return (
    <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center gap-1" aria-hidden>
      {nodes.map((n, i) => (
        <span key={i} className={`${REVEAL} h-2 w-2 ${n.delay} ${n.bg} ${n.glow}`} />
      ))}
    </div>
  );
}

/** 03 Modos — 7 nodos en círculo, sentido horario */
function ModosGlyph() {
  const count = 7;
  const radius = 14;

  return (
    <div className="relative h-10 w-10 shrink-0" aria-hidden>
      {Array.from({ length: count }, (_, i) => {
        const angleDeg = (i / count) * 360 - 90;
        const isHighlight = i === 0;
        return (
          <span
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              transform: `translate(-50%, -50%) rotate(${angleDeg}deg) translateY(-${radius}px)`,
            }}
          >
            <span
              className={`${REVEAL} ${DELAYS[i]} ${
                isHighlight
                  ? "h-2.5 w-2.5 bg-purple-400 group-hover:shadow-[0_0_15px_rgba(192,132,252,0.95)]"
                  : "h-1.5 w-1.5 bg-white group-hover:shadow-[0_0_8px_rgba(255,255,255,0.75)]"
              }`}
            />
          </span>
        );
      })}
    </div>
  );
}

/** 04 Notas — pulso eléctrico menta + anillo expansivo */
function NotasGlyph() {
  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center" aria-hidden>
      <span
        className="absolute h-6 w-6 rounded-full border border-emerald-400/30 opacity-0 scale-75 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:scale-[1.45] group-hover:border-emerald-400/70 group-hover:shadow-[0_0_14px_rgba(52,211,153,0.45)] delay-75"
      />
      <span
        className={`${REVEAL} h-2.5 w-2.5 bg-emerald-400 delay-0 group-hover:!scale-125 group-hover:shadow-[0_0_16px_rgba(52,211,153,0.95)]`}
      />
    </div>
  );
}

/** 05 Melodías — zigzag horizontal secuencial */
function MelodiesGlyph() {
  const lifts = [-5, 7, -2, 5];
  return (
    <div className="flex h-10 w-14 shrink-0 items-center justify-end gap-2" aria-hidden>
      {lifts.map((lift, i) => (
        <span key={i} className="inline-flex shrink-0" style={{ transform: `translateY(${lift}px)` }}>
          <span
            className={`${REVEAL} h-2 w-2 bg-white ${DELAYS[i]} group-hover:shadow-[0_0_11px_rgba(196,181,253,0.9)]`}
          />
        </span>
      ))}
    </div>
  );
}

/** 06 Songbook — líneas que se expanden desde el centro, luego puntos */
function SongbookGlyph() {
  return (
    <div className="relative flex h-10 w-12 shrink-0 flex-col items-center justify-center gap-2" aria-hidden>
      <span
        className="h-px w-0 transition-all duration-300 ease-out delay-0 group-hover:w-8 group-hover:shadow-[0_0_8px_rgba(196,181,253,0.6)]"
        style={{ backgroundColor: `${T.lavender}cc` }}
      />
      <span
        className="h-px w-0 bg-white/80 transition-all duration-300 ease-out delay-75 group-hover:w-8 group-hover:shadow-[0_0_8px_rgba(255,255,255,0.5)]"
      />
      <span
        className={`absolute left-2 top-[calc(50%-5px)] ${REVEAL} h-1.5 w-1.5 bg-white delay-150 group-hover:shadow-[0_0_10px_rgba(255,255,255,0.85)]`}
      />
      <span
        className={`absolute right-2 top-[calc(50%+4px)] ${REVEAL} h-1.5 w-1.5 delay-200 group-hover:shadow-[0_0_10px_rgba(196,181,253,0.9)]`}
        style={{ backgroundColor: T.lavender }}
      />
    </div>
  );
}

const GLYPHS: Record<HubModuleVisualId, React.FC> = {
  guitar: GuitarTechniqueGlyph,
  scales: ScalesGlyph,
  harmony: HarmonyGlyph,
  chords: ChordsGlyph,
  modos: ModosGlyph,
  notas: NotasGlyph,
  melodies: MelodiesGlyph,
  songbook: SongbookGlyph,
  library: PartiturasGlyph,
};

export const HubModuleVisual: React.FC<HubModuleVisualProps> = ({ moduleId }) => {
  const Glyph = GLYPHS[moduleId as HubModuleVisualId];
  if (!Glyph) return null;
  return <Glyph />;
};
